// Client 공급가 계산 API

import apiClient from './axios';
import { useDummyDataConfig } from '../config/dummyDataConfig';
import {
  exchangeRatesDummyData,
  estimateCalculatorDummyData,
  clientSettingsDummyData
} from '../data/clientPricingDummyData';
import { setRoundingModes, roundingModeFor, normalizeRoundingModes, DEFAULT_ROUNDING } from '../config/roundingMode';
import { hydrateAddons } from '../config/addonItems';

/**
 * 받은 요금 데이터의 통화 줄마다 반올림 방식을 붙여 둔다(2026-09-30 — 반올림 기본, 올림은 나라·통화별 선택).
 * 계산하는 화면들이 통화 줄(rate/estimate)을 그대로 들고 다니므로, 줄에 붙여 두면 어느 화면이든 같은 방식을 쓴다.
 * 서버가 아직 roundingMode 를 안 주면(배포 전) 전부 반올림이다.
 */
const withRoundingModes = (data) => {
  if (!data || typeof data !== 'object') return data;
  const modes = setRoundingModes(data.roundingMode);
  return {
    ...data,
    roundingMode: modes,
    exchangeRates: (data.exchangeRates || []).map((r) => ({ ...r, roundingMode: roundingModeFor(r.from, modes) })),
    estimates: (data.estimates || []).map((e) => ({ ...e, roundingMode: roundingModeFor(e.startCurrency, modes) })),
  };
};

/**
 * 테이블 1: 환율 설정 목록 조회
 */
export const getExchangeRates = async () => {
  if (useDummyDataConfig.exchangeRates) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(exchangeRatesDummyData);
      }, 300);
    });
  }

  const response = await apiClient.get('/api/client-pricing/exchange-rates');
  return response.data;
};

/**
 * 테이블 1: 환율 설정 저장
 */
/* session: 저장 단추에서 이미 받은 확인(askCriticalConfirm) — 있으면 확인 창을 또 띄우지 않는다(2026-10-23 확인창 개편 2단계) */
export const saveExchangeRates = async (exchangeRates, session) => {
  if (useDummyDataConfig.exchangeRates) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: true,
          message: '환율 설정이 저장되었습니다.'
        });
      }, 300);
    });
  }

  const body = { exchangeRates };
  const response = session
    ? await apiClient.post('/api/client-pricing/exchange-rates', body, { criticalSession: session })
    : await apiClient.post('/api/client-pricing/exchange-rates', body);
  return response.data;
};

/**
 * 테이블 2: 예상 계산기 데이터 조회
 */
export const getEstimateCalculator = async () => {
  if (useDummyDataConfig.estimateCalculator) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(estimateCalculatorDummyData);
      }, 300);
    });
  }

  const response = await apiClient.get('/api/client-pricing/estimate-calculator');
  return response.data;
};

/**
 * 테이블 3: 개별 클라이언트 계산 설정 조회
 */
export const getClientSettings = async () => {
  if (useDummyDataConfig.clientSettings) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(clientSettingsDummyData);
      }, 300);
    });
  }

  const response = await apiClient.get('/api/client-pricing/client-settings');
  return response.data;
};

/**
 * 테이블 3: 개별 클라이언트 계산 설정 저장
 */
export const saveClientSettings = async (clientSettings, session) => {
  if (useDummyDataConfig.clientSettings) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: true,
          message: '클라이언트 설정이 저장되었습니다.'
        });
      }, 300);
    });
  }

  const body = { clientSettings };
  const response = session
    ? await apiClient.post('/api/client-pricing/client-settings', body, { criticalSession: session })
    : await apiClient.post('/api/client-pricing/client-settings', body);
  return response.data;
};

/**
 * 전체 Client 공급가 계산 데이터 조회
 */
export const getAllClientPricingData = async () => {
  if (useDummyDataConfig.clientPricing) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: true,
          data: {
            exchangeRates: exchangeRatesDummyData.data.exchangeRates,
            estimates: estimateCalculatorDummyData.data.estimates,
            clientSettings: clientSettingsDummyData.data.clientSettings
          }
        });
      }, 300);
    });
  }

  /* 부가 항목 기준도 같이 받아 둔다 — 계산하는 화면들이 loadAddons() 로 서버 값을 쓰게(실패해도 계속) */
  const [response] = await Promise.all([
    apiClient.get('/api/client-pricing/all'),
    hydrateAddons().catch(() => null),
  ]);
  const body = response.data;
  if (body && body.data) return { ...body, data: withRoundingModes(body.data) };
  return body;
};

/**
 * 반올림 방식 설정 조회 — { default: 'round', byCurrency: { THB: 'ceil' } }
 * 서버에 아직 없으면(404 등) 기본값(전부 반올림)을 돌려준다 — 화면이 깨지지 않게.
 */
export const getRoundingMode = async () => {
  try {
    const response = await apiClient.get('/api/client-pricing/rounding-mode');
    const raw = response.data && 'data' in response.data ? response.data.data : response.data;
    return setRoundingModes(raw);
  } catch (e) {
    return normalizeRoundingModes(DEFAULT_ROUNDING);
  }
};

/** 반올림 방식 설정 저장 (MASTER) — 저장이 끝나면 이 브라우저의 계산도 바로 새 방식을 쓴다 */
export const saveRoundingMode = async (modes, session) => {
  const body = normalizeRoundingModes(modes);
  const response = session
    ? await apiClient.post('/api/client-pricing/rounding-mode', body, { criticalSession: session })
    : await apiClient.post('/api/client-pricing/rounding-mode', body);
  setRoundingModes(body);
  return response.data;
};

/**
 * 전체 Client 공급가 계산 데이터 저장
 */
export const saveAllClientPricingData = async (data) => {
  if (useDummyDataConfig.clientPricing) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          success: true,
          message: '모든 설정이 저장되었습니다.'
        });
      }, 300);
    });
  }

  const response = await apiClient.post('/api/client-pricing/all', data);
  return response.data;
};

/**
 * 개별 클라이언트 계산 히스토리 조회 (어드민 전용)
 */
export const getClientPricingHistory = async ({ clientId, search, page = 1, limit = 20 }) => {
  const params = { clientId, page, limit };
  if (search) params.search = search;
  const response = await apiClient.get('/api/client-pricing/history', { params });
  return response.data;
};

/**
 * 히스토리 검색 드롭다운 — 캠페인 배정 인플루언서 KOL ID 목록
 */
export const getClientPricingHistoryInfluencerOptions = async (campaignId) => {
  const response = await apiClient.get('/api/client-pricing/history/influencer-options', {
    params: { campaignId },
  });
  return response.data;
};
